import { EditableComponent } from '@adobe/aem-react-editable-components';
import {
  ADivider,
  ADividerOrientation,
  ALinkStandalone,
  ALinkStandaloneSize,
  ASubheader,
  ASubheaderSize,
} from '@marriott/mi-web-ui-kit';
import { StyledTableOfContents } from './TableOfContents.styles';
import { tableOfContentsMockData } from './__mock__/TableOfContents.mock';
import { TableOfContentsListItemProps, TableOfContentsLinkProps, TableOfContentsProps } from './TableOfContents.types';

const { NEXT_PUBLIC_AEM_SITE } = process.env;

const mapModelLinksToLinkProps = (items?: TableOfContentsListItemProps[]): TableOfContentsLinkProps[] =>
  (items ?? []).map(item => ({
    linkText: item?.heading,
    ctaLink: item?.id,
    openInNewTab: false,
  }));

export const TableOfContents = ({
  title,
  links,
  tableofcontents,
  tableOfContentsLabel,
  tableOfContentsList,
  styleclass,
  isAuthorMode,
}: TableOfContentsProps) => {
  const model = isAuthorMode ? tableOfContentsMockData : tableofcontents;

  const resolvedTitle = model?.tableOfContentsLabel ?? tableOfContentsLabel ?? title;
  const resolvedLinks = model?.tableOfContentsList
    ? mapModelLinksToLinkProps(model.tableOfContentsList)
    : tableOfContentsList
    ? mapModelLinksToLinkProps(tableOfContentsList)
    : links;
  const validLinks = (resolvedLinks ?? []).filter(item => Boolean(item?.linkText));

  const hasContent = Boolean(resolvedTitle || validLinks.length);

  if (!hasContent) {
    return null;
  }

  return (
    <StyledTableOfContents
      data-testid="table-of-contents"
      data-component-name="o-awp-table-of-contents"
      className={`${styleclass} my-5`}
    >
      <ADivider orientation={ADividerOrientation.Vertical} className="toc-divider" aria-hidden="true" />
      <div className="toc-content my-5">
        {resolvedTitle && <ASubheader text={resolvedTitle} size={ASubheaderSize.XL} className="mb-0" />}

        {validLinks.length ? (
          <ul className="toc-list">
            {validLinks.map((item, index) => {
              const linkTarget = item?.ctaLink || '#';
              const isInPageAnchor = linkTarget.startsWith('#');
              const shouldOpenInNewTab = isInPageAnchor ? false : item?.openInANewTab ?? item?.openInNewTab ?? false;

              return (
                <li className="toc-item" key={`${item?.linkText}-${index}`}>
                  <ALinkStandalone
                    text={item?.linkText}
                    ctaLink={linkTarget}
                    openInANewTab={shouldOpenInNewTab}
                    size={ALinkStandaloneSize.Medium}
                    className="toc-link"
                  />
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </StyledTableOfContents>
  );
};

export const TableOfContentsConfig = {
  emptyLabel: 'TableOfContents',
  isEmpty: () => false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/traveler/tableofcontents`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TableOfContentsEditable = (props: any) => (
  <EditableComponent config={TableOfContentsConfig} {...props}>
    <TableOfContents {...props} />
  </EditableComponent>
);
